// General
import React, { useState, useContext } from "react";

// Context
import { CashContext } from "../data/cash/CashContext";
import { useData } from "../data/general/DataContext";

// Pages / Core Components
import TablePage from "../core/TablePage";
import DonutChart from "../core/DonutChart";

// Modals
import CashAddModal from "../modals/cash/CashAddModal";
import CashUpdateModal from "../modals/cash/CashUpdateModal";

// UI Components / Icons
import NotificationIcons from "../components/NotificationIcon";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { FaTrash, FaPencilAlt } from "react-icons/fa";

function CashPage() {
  const { cashList, addCash, updateCash, deleteCash } = useContext(CashContext);
  const { windowMode } = useData();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCash, setSelectedCash] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const filteredCash = cashList;

  // Masaüstü için OverlayPanel ActionMenu
  const ActionMenu = ({ rowData }) => {
    const op = React.useRef(null);
    return (
      <>
        <Button
          label=". . ."
          className="p-button-rounded p-button-text"
          style={{
            minWidth: "40px",
            padding: "8px 8px 12px",
            fontSize: "22px",
            fontWeight: "bold",
          }}
          onClick={(e) => op.current.toggle(e)}
        />
        <OverlayPanel
          ref={op}
          dismissable
          showCloseIcon={false}
          style={{ padding: "0.5rem", left: "-5px" }}
        >
          <div className="flex flex-col gap-0 p-2 w-32">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="p-button-text p-button-lg w-full justify-start"
              style={{ fontSize: "14px", gap: "10px" }}
              onClick={() => {
                setSelectedCash(rowData);
                setShowUpdateModal(true);
                op.current.hide();
              }}
            />
            <div className="w-full border-t border-gray-300 my-1"></div>
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="p-button-text p-button-lg w-full justify-start"
              style={{ color: "red", fontSize: "14px", gap: "10px" }}
              onClick={() => {
                deleteCash(rowData.id);
                op.current.hide();
              }}
            />
          </div>
        </OverlayPanel>
      </>
    );
  };

  // Kolonlar
  const columns = [
    { field: "id", header: "ID", hideInFilter: true },
    { field: "date", header: "Tarih", hideInFilter: true },
    { field: "paymentType", header: "Ödeme Türü" },
    { field: "paymentDirection", header: "Gelir/Gider" },
    { field: "paymentMethod", header: "Şekil" },
    { field: "status", header: "Durum", hideInFilter: true },
    {
      field: "amount",
      header: "Tutar",
      hideInFilter: true,
      body: (rowData) => (
        <span>
          {Number(rowData.amount).toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
          })}{" "}
          TL
        </span>
      ),
    },
    { field: "description", header: "Açıklama", hideInFilter: true },
    {
      header: "İşlemler",
      body: (rowData) => {
        if (windowMode === "mobile") {
          return (
            <div className="flex gap-2 mt-2 w-full">
              <Button
                icon={<FaPencilAlt />}
                label="Düzenle"
                className="flex-1"
                style={{
                  backgroundColor: "#3b82f6", 
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "12px",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "0 6px",
                }}
                onClick={() => {
                  setSelectedCash(rowData);
                  setShowUpdateModal(true);
                }}
              />
              <Button
                icon={<FaTrash />}
                label="Sil"
                className="flex-1"
                style={{
                  backgroundColor: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "12px",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "0 6px",
                }}
                onClick={() => deleteCash(rowData.id)}
              />
            </div>
          );
        }
        return <ActionMenu rowData={rowData} />;
      },
      hideInFilter: true,
    },
  ];

  const enhancedColumns = columns.map((col) =>
    col.field === "id"
      ? { ...col, style: { width: "60px", padding: "0 12px" } }
      : { ...col, style: { padding: "0 12px" } }
  );

  const columnFilterOptions = {
    paymentDirection: { type: "checkbox", options: ["Gelir", "Gider"] },
    paymentType: { type: "checkbox", options: ["Satış", "Kira"] },
    paymentMethod: { type: "checkbox", options: ["Nakit", "Havale"] },
  };

  return (
    <>
      {/* Başlık + ikonlar */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3
            style={{
              fontWeight: 600,
              fontSize: "21px",
              lineHeight: "26px",
            }}
          >
            Kasa Listesi
          </h3>
          <p
            style={{
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              color: "#6B7280",
              marginTop: "0px",
            }}
          >
            Kasa kayıtlarını ekleyebilir, düzenleyebilir veya silebilirsiniz.
          </p>
        </div>
        <NotificationIcons notificationCount={3} />
      </div>

      {/* Kart + Grafik + Tablo */}
      <div
        className="cash-card-wrapper"
        style={{
          width: "100%",
          borderRadius: "15px",
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DonutChart>
          {/* Tabloyu Chart’ın altına gömüyoruz */}
          <div className="cash-table-wrapper mt-4">
            <TablePage
              columns={enhancedColumns}
              data={filteredCash}
              defaultRows={5}
              rowsPerPageOptions={[5, 10, 15]}
              showSearch
              rounded={false}
              searchValue={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              onAdd={() => setShowAddModal(true)}
              showDateFilter
              columnFilterOptions={columnFilterOptions}
              scrollableControls={true}
              hideHeaderRight={true}
              searchStyle={{ marginLeft: "10px" }} 
              cashPageStyle={{ marginRight: "10px" }}


            />
          </div>
        </DonutChart>
      </div>

      <CashAddModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        onSubmit={addCash}
      />
      <CashUpdateModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        cash={selectedCash}
      />
    </>
  );
}

export default CashPage;
